import {inject} from '@angular/core';
import {Router, UrlTree} from '@angular/router';
import {FireAuthService} from '../../shared-utilities/services-old/fire-auth.service';
import {Store} from '@ngrx/store';
import {AccessFlags, pageRules} from '../../shared-utilities/models-old/utils-old/rule-structure';
import {ISharedModalBasic} from '../shared-models/modals/shared-modal-basic';
import {IconColourEnum} from '../../shared-modules/shared-icons/models/icons-colour.enum';
import {
  SharedModalBasicComponent,
} from '../shared-components/components/shared-modal-basic/shared-modal-basic.component';
import {selectUserAccess} from '../../features/user-access/user-access-store/user-access.selectors';
import {IUser} from '../../features/user-access/models/user/user';
import {selectUser} from '../../features/user-access/user-store/user.selectors';
import {IRuleInfo} from '../../features/user-access/models/rule-info';
import {RuleHumanID} from '../../features/user-access/models/rule-info.enum';
import {IUserAccess} from '../../features/user-access/models/user-access';
import {IconsEnum} from '../../shared-modules/shared-icons/models/icons.enum';
import {ModalControllerService} from '../shared-services/overlay/modal-controller.service';
import {ENABLE_PRODUCT_MAPPING_FEATURE, ENABLE_QUOTES_FEATURE} from '../../shared-utilities/models-old/page/pages-const';
import {NavigationPageEnum} from '../../features/user-access/models/navigations.enums';

/**
 * Route path used for access rules: query string stripped, no leading slash.
 * Matches `canActivateGuard` / `canActivateChildGuard` (`state.url.split('?')[0].slice(1)`).
 */
export const getAccessPageFromUrl = (url: string): string => {
  const path: string = (url ?? '').split('?')[0];
  return path.startsWith('/') ? path.slice(1) : path;
};

/**
 * True when the user has at least one of the page rules for the given store.
 */
export const storeHasAnyPageRule = (
  authService: FireAuthService,
  storeId: string,
  pageRuleIds: (RuleHumanID | AccessFlags)[],
): boolean => {
  if (!storeId || pageRuleIds.length === 0) {
    return pageRuleIds.length === 0;
  }
  return pageRuleIds.some(
    (ruleID: RuleHumanID | AccessFlags) => authService.hasAccess(storeId, {ruleID}) === true,
  );
};

/**
 * True when the selected store has access to the current page.
 * Home, login, and pages without rule IDs always return true.
 */
export const canSelectStoreOnCurrentPage = (
  authService: FireAuthService,
  storeId: string | undefined,
  currentPage: string,
): boolean => {
  if (
    !storeId ||
    !currentPage ||
    currentPage === NavigationPageEnum.Home ||
    currentPage === NavigationPageEnum.Login ||
    currentPage.startsWith(`${NavigationPageEnum.Home}/`) ||
    currentPage.startsWith(`${NavigationPageEnum.Login}/`)
  ) {
    return true;
  }
  const pageRuleIds: false | (RuleHumanID | AccessFlags)[] = pageRules(currentPage);
  if (!Array.isArray(pageRuleIds) || pageRuleIds.length === 0) {
    return true;
  }
  return storeHasAnyPageRule(authService, storeId, pageRuleIds);
};

/**
 * Validates if the user has the necessary access to a given page.
 *
 * Access is allowed when the user has the page on at least one store.
 * If the selected store lacks the page, the feature route still loads and the
 * shell shows a no-access state until they change store.
 *
 * @param {string} page - The page identifier to validate access for.
 * @returns {Promise<boolean | UrlTree>} - Returns `true` if the user has access,
 *         otherwise returns a `UrlTree` for redirecting the user.
 */
export const validateAccess = async (page: string): Promise<boolean | UrlTree> => {
  if (page.startsWith('/auth')) {
    return true;
  }

  const router: Router = inject(Router);

  if (!ENABLE_QUOTES_FEATURE && (page === 'quotes' || page.startsWith('quotes/'))) {
    return router.createUrlTree(['/']);
  }

  if (!ENABLE_PRODUCT_MAPPING_FEATURE && (page === 'stock-match' || page.startsWith('stock-match/'))) {
    return router.createUrlTree(['/']);
  }

  const authService: FireAuthService = inject(FireAuthService);
  const store: Store = inject(Store);
  let userAccess: IUserAccess | null = null;

  store.select(selectUserAccess)
    .subscribe((access: IUserAccess): void => {
      userAccess = access && Object.keys(access).length > 0 ? access : null;
    });

  const pageRuleIds: false | (RuleHumanID | AccessFlags)[] = pageRules(page);

  if (pageRuleIds !== false && pageRuleIds.length === 0) return true;
  if (!userAccess?.storeList) return false;
  if (pageRuleIds === false) return false;

  const failed: IRuleInfo[] = [];
  let hasAnyStoreAccess = false;

  for (const ruleID of pageRuleIds) {
    let ruleAllowed = false;
    let failureInfo: IRuleInfo | null = null;

    for (const storeId of userAccess?.storeList || []) {
      const result = authService.hasAccess(storeId, {ruleID});
      if (result === true) {
        ruleAllowed = true;
        hasAnyStoreAccess = true;
        break;
      } else {
        failureInfo = result;
      }
    }

    if (!ruleAllowed && failureInfo) {
      failed.push(failureInfo);
    }
  }

  if (!hasAnyStoreAccess) {
    const failedText: string[] = failed.reduce((acc: string[], ruleInfo: IRuleInfo) => {
      acc.push(ruleInfo.humanID);
      return acc;
    }, []);
    await displayInsufficientAccessModal(failedText);
    return router.createUrlTree(['/']);
  }

  return true;
};

/**
 * Displays a modal indicating insufficient access to the user.
 */
const displayInsufficientAccessModal = async (
  failedPages: string[],
): Promise<void> => {
  const modalController: ModalControllerService = inject(ModalControllerService);
  const componentProps: ISharedModalBasic = {
    buttonAccept: false,
    buttonAcceptText: '',
    buttonClose: true,
    buttonCloseCross: false,
    buttonCloseText: `Confirm`,
    modalTitle: 'Insufficient Access',
    modalTitleIcon: IconsEnum.infoSlabCircleOutline,
    modalTitleIconColor: IconColourEnum.red,
    contextHeading: `NOTE: You do not have permission to access the following.`,
    contextDescription: [
      ...failedPages,
    ],
  };
  const modalControl = await modalController.create({
    backdropDismiss: false,
    component: SharedModalBasicComponent,
    componentProps,
    cssClass: ['shared-basic-modal-css'],
  });
  await modalControl.present();
};

/**
 * Checks if a user is logged in by verifying the presence of a user ID in the store.
 */
export const isUserLoggedIn = async (): Promise<boolean> => {
  const store: Store = inject(Store);
  let loggedInUser: IUser | null = null;
  store.select(selectUser)
    .subscribe((user: IUser): void => {
      loggedInUser = user?.id ? user : null;
    });
  return loggedInUser?.id === null;
};
